import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import API from "../utils/API";
import "./peopleFeed.css";
import "./divisionlist.css";
import "./divisionStaffList.css";
import defaultPortrait from "../assets/portrait-5.png";

const OFFICE_FILTERS = [
    { label: "Capitol Office", value: "Capitol" },
    { label: "District Office", value: "District" },
];

function PersonCard({ person, officeTag }) {
    const navigate = useNavigate();
    const info = person.ProfileInfo;
    const phone = info.other_phone || info.mobile_phone;

    return (
        <div className="people-card" onClick={() => navigate("/profile/" + info.profile_id)}>
            <img src={info.profile_picture || defaultPortrait}
                className="people-card-picture"
                alt={`${info.first_name} ${info.last_name}`}
            />

            <div className="people-card-details">
                <span className="people-card-name">{info.first_name} {info.last_name}</span>
                {person.title && (
                    <span className="people-card-title">{person.title}</span>
                )}
                {info.work_email && (
                    <a href={"mailto:" + info.work_email}
                        className="people-card-email"
                        onClick={(e) => e.stopPropagation()}
                    >{info.work_email}</a>
                )}
                {(phone || officeTag) && (
                    <span className="people-card-bottom-row">
                        {phone && <span className="people-card-phone">{phone}</span>}
                        {officeTag && <span className="people-card-office-tag">{officeTag}</span>}
                    </span>
                )}
            </div>
        </div>
    );
}

function getOfficeTag(person) {
    if (person.office_type === "District") {
        return person.city || "District";
    }
    if (person.office_type === "Capitol") {
        return "Capitol";
    }
    return undefined;
}

export default function DivisionStaffList({ divisionId, isLegislative }) {
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [officeFilterSelect, setOfficeFilterSelect] = useState(undefined);

    useEffect(() => {
        if (!divisionId) return;
        setLoading(true);
        API.getDivisionColleagues({ division_id: divisionId })
            .then(res => { setPeople(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, [divisionId]);

    const elected = useMemo(() => people.filter(p => p.office_type === "Elected"), [people]);
    const capitol = useMemo(() => people.filter(p => p.office_type === "Capitol"), [people]);
    const district = useMemo(() => people.filter(p => p.office_type === "District"), [people]);
    const other = useMemo(() => people.filter(p =>
        !["Elected", "Capitol", "District"].includes(p.office_type)
    ), [people]);

    const staffPool = useMemo(() => [...capitol, ...district, ...other], [capitol, district, other]);

    const filteredStaff = useMemo(() => {
        if (officeFilterSelect === undefined) return staffPool;
        return staffPool.filter(p => p.office_type === officeFilterSelect);
    }, [staffPool, officeFilterSelect]);

    const showOfficeFilters = isLegislative && capitol.length > 0 && district.length > 0;

    if (loading) {
        return (
            <div className="py-4 text-center">
                <div className="spinner-border loading-spinner" role="status">
                    <span className="visually-hidden">Loading…</span>
                </div>
            </div>
        );
    }

    return (
        <>
            {isLegislative && elected.length > 0 && (
                <div className="dsl-elected-section">
                    <h6 className="dsl-section-title">Elected</h6>
                    <div className="people-card-grid mb-4">
                        {elected.map(p => <PersonCard key={p.profile_id} person={p} />)}
                    </div>
                </div>
            )}

            <h6 className="org-selectors">
                <span className="me-5 org-feed-selector selected">People</span>
            </h6>
            <hr className="mb-2"/>

            <div className="org-people-feed mt-sm-0 mt-0 pt-0">
                {showOfficeFilters && (
                    <div className="division-filter-bar">
                        {OFFICE_FILTERS.map(filter => (
                            <button key={filter.value}
                                className={`division-filter-button ${officeFilterSelect === filter.value ? "selected" : ""}`}
                                onClick={() => setOfficeFilterSelect(filter.value)}
                            >{filter.label}</button>
                        ))}
                        <button className="clear-division-filter-button" onClick={() => setOfficeFilterSelect(undefined)}>
                            Reset
                        </button>
                    </div>
                )}

                {filteredStaff.length === 0 ? (
                    <div className="py-2 ms-3 text-muted small">No staff records found.</div>
                ) : (
                    <div className="people-card-grid">
                        {filteredStaff.map(p => (
                            <PersonCard key={p.profile_id} person={p} officeTag={getOfficeTag(p)} />
                        ))}
                    </div>
                )}
            </div>
        </>
    );
}
